import * as Survey from "survey-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { GITSCategoryActions, IInfraConfig } from "../redux";
import { useSelectorTyped } from "../../../../common/redux/store";
import getInfraConfigQuestions from "./data/questions";
import { stepLabels } from "../../utils/step-labels";
import { resolveGitsGithubOrganisations } from "../../data/team-github-orgs";
import YesNoChoices from "../../data/yes-no-choice";

export default function useInfraConfigController() {
  const dispatch = useDispatch();
  const infraConfigFormData = useSelectorTyped((state) => state.getStartedPage.gits.infraConfig);
  const { projectName, teamId, teamName } = useSelectorTyped((state) => state.getStartedPage.gits.basicInfo);
  /** `null` while fetching; `[]` when BFF returned no orgs; otherwise resolved list. */
  const [gitsGithubOrganisationChoices, setGitsGithubOrganisationChoices] = useState<string[] | null>(null);

  useEffect(() => {
    if (!teamId) {
      setGitsGithubOrganisationChoices(null);
      return;
    }
    dispatch(GITSCategoryActions.updateInfraConfig({ githubOrganisation: "" }));
    let cancelled = false;
    setGitsGithubOrganisationChoices(null);
    void (async () => {
      const orgs = await resolveGitsGithubOrganisations(teamId);
      if (!cancelled) {
        setGitsGithubOrganisationChoices(orgs);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, dispatch]);

  useEffect(() => {
    if (!teamId || gitsGithubOrganisationChoices === null) {
      return;
    }
    const initialOrg = gitsGithubOrganisationChoices[0] ?? "";
    dispatch(GITSCategoryActions.updateInfraConfig({ githubOrganisation: initialOrg }));
  }, [dispatch, gitsGithubOrganisationChoices, teamId]);

  const onInfraSurveyValueChanged = useCallback((sender: Survey.SurveyModel) => {
    dispatch(GITSCategoryActions.updateInfraConfig(sender.data as Partial<IInfraConfig>));
  }, [dispatch]);

  const isGithubRepoYesWithoutMappedOrg = () =>
    infraConfigFormData.isCodeRepositoryNeeded === YesNoChoices.YES
    && gitsGithubOrganisationChoices !== null
    && gitsGithubOrganisationChoices.length === 0;

  const isNextButtonDisabled = isGithubRepoYesWithoutMappedOrg()
    && infraConfigFormData.isHostingServiceNeeded === YesNoChoices.NO;

  const shouldGoToNextStep = (formData: Partial<IInfraConfig>) => {
    dispatch(GITSCategoryActions.updateInfraConfig(formData));
    if (
      formData.isCodeRepositoryNeeded === YesNoChoices.YES
      && gitsGithubOrganisationChoices !== null
      && gitsGithubOrganisationChoices.length === 0
    ) {
      return formData.isHostingServiceNeeded === YesNoChoices.YES;
    }
    return true;
  };

  const shouldGoToPreviousStep = (formData: Partial<IInfraConfig>) => {
    dispatch(GITSCategoryActions.updateInfraConfig(formData));
    return true;
  };

  const surveyFormData = useMemo(() => {
    const data = { ...infraConfigFormData };
    if (gitsGithubOrganisationChoices !== null && gitsGithubOrganisationChoices.length === 1) {
      data.githubOrganisation = gitsGithubOrganisationChoices[0];
    }
    return data;
  }, [infraConfigFormData, gitsGithubOrganisationChoices]);

  return {
    questions: getInfraConfigQuestions(
      projectName,
      gitsGithubOrganisationChoices,
    ),
    errorText: null,
    teamDetails: { teamId: teamId ?? "", teamName: teamName ?? "" },
    progressText: null,
    shouldRenderForm: true,
    infraConfigFormData,
    surveyFormData,
    shouldGoToNextStep,
    shouldGoToPreviousStep,
    shouldRenderError: () => false,
    getStepLabelsForPage: () => stepLabels,
    onInfraSurveyValueChanged,
    isNextButtonDisabled,
  };
}
